import React, { useState, useEffect } from 'react'

import Grid2 from '@mui/material/Unstable_Grid2'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'

import FormattedInputs from './mobile_number_text_field'
import DatePickerField from './date_picker_field'
import dayjs, { Dayjs } from 'dayjs'
import PlainTextField from './plain_text_field'
import UserService, { AddUserRequestProps, Result } from '../services/user_service'
import DocumentIDTextField from './document_id_text_field'
import DropdownSelectField, { DropdownItem } from './dropdown_select_field'
import useGlobalState from '../global_state'
import { Alert, Box, Card, CircularProgress, Fade, Stack, Typography } from '@mui/material'
import { SnackbarProvider, VariantType, useSnackbar } from 'notistack'
import CarNumberTextField from './car_number_text_field'
import { useAuth } from '../providers/auth_provider'

enum Loading {
    loading,
    success,
    failed,
    loaded
}
interface AddEntryErrors {
    readonly mobileNumber?: string | null,
    readonly firstName?: string | null,
    readonly lastName?: string | null,
    readonly documentID?: string | null,
    readonly carNumber?: string | null
}

const ONE_MONTH_IN_SECONDS = 2_629_743

const paymentItems: DropdownItem[] = [
    { value: '1', label: "Цялостно плащане" },
    { value: '2', label: "(2) Две вноски" },
    { value: '4', label: "(4) Четири вноски" },
]

const AddEntryComponent = () => {
    const { token, logout } = useAuth()
    const userService = new UserService(token)

    // const [usersByYearAndMonth, setUsersByYearAndMonth] = useGlobalState('usersByYearAndMonth')
    const [mobileNumber, setMobileNumber] = React.useState<string>('')
    const [date, setDate] = React.useState<Dayjs | null>(dayjs(new Date()))
    const [firstName, setFirstName] = React.useState<string>('')
    const [lastName, setLastName] = React.useState<string>('')
    const [errors, setErrors] = useState<AddEntryErrors>({})
    const [documentID, setDocumentID] = useState<string>('')
    const [carNumber, setCarNumber] = useState<string>('')
    const [paymentsCount, setPaymentsCount] = useState<DropdownItem>(paymentItems[0])
    const [loading, setLoading] = useState<Result<Loading, string>>(Result.success<Loading, string>(Loading.loaded))
    const [message, setMessage] = useState<React.ReactNode>(null)
    const { enqueueSnackbar } = useSnackbar()

    const isLoading = (): boolean => {
        if (loading.isSuccess == false) {
            return false
        } else {
            return loading.value === Loading.loading
        }
    }

    const mainError = (): string | undefined => Object.values(errors).reduce((prev, cur) => {
        if (prev != undefined) {
            return prev
        } else {
            return cur
        }
    }, undefined)

    const prepareRequestData = (): AddUserRequestProps => {
        return {
            mobileNumber: mobileNumber.replace(/\s/g, ""),
            firstName,
            lastName,
            documentID,
            downpaymentCount: paymentsCount.value,
            carNumber: carNumber.trim().replaceAll(" ", ""),
            fromDate: Math.round(date!.unix() * 1000)
        }
    }

    const updatePaymentCounts = (value: DropdownItem) => {
        setPaymentsCount(value)
    }

    const updateDocumentID = (value: string) => {
        setDocumentID(value)
        setErrors({ ...errors, documentID: null })
    }

    const updateCarNumber = (value: string) => {
        setCarNumber(value)
        setErrors({ ...errors, carNumber: null })
    }

    const updateMobileNumber = (value: string) => {
        setMobileNumber(value)
        setErrors({ ...errors, mobileNumber: null })
    }

    const updateDate = (value: Dayjs | null) => {
        setDate(value)
    }

    const updateFirstName = (value: string) => {
        setFirstName(value)
        setErrors({ ...errors, firstName: null })
    }

    const updateLastName = (value: string) => {
        setLastName(value)
        setErrors({ ...errors, lastName: null })
    }

    const isValidForm = (): boolean => {
        let isValid = true
        let validationErrors: AddEntryErrors = {}

        if (mobileNumber.trim().split(" ").join().length != 11) {
            validationErrors = { ...validationErrors, mobileNumber: "Невалиден телефонен номер" }
            isValid = false
        }

        if (date?.isValid() != true) {
            isValid = false
        }

        if (firstName.trim() == "") {
            validationErrors = { ...validationErrors, firstName: "Невалидно име" }
            isValid = false
        }

        if (lastName.trim() == "") {
            validationErrors = { ...validationErrors, lastName: "Невалидна фамилия" }
            isValid = false
        }

        if (documentID.trim() == "") {
            validationErrors = { ...validationErrors, documentID: "Неправилен номер на полица" }
            isValid = false
        }

        const carNumberStripped = carNumber.replaceAll(" ", "")
        if (carNumberStripped.trim() == "" || carNumberStripped.length < 6 || carNumberStripped.length > 8) {
            validationErrors = { ...validationErrors, carNumber: "Неправилен номер на кола" }
            isValid = false
        }

        setErrors(validationErrors)
        return isValid
    }

    const resetForm = () => {
        setCarNumber("")
        setDocumentID("")
        setFirstName("")
        setLastName("")
        setPaymentsCount(paymentItems[0])
        setMobileNumber("")
        setErrors({})
    }

    const handleSubmit = async () => {
        if (isValidForm()) {
            setLoading(Result.success(Loading.loading))

            const result = await userService.addEntry(prepareRequestData())
            resetForm()

            if (result.isSuccess) {
                setLoading(Result.success(Loading.success))
            } else {
                setLoading(Result.error(result.error!))
            }


            setTimeout(() => {
                setLoading(Result.success(Loading.loaded))
            }, 5000)
        }
    }

    const updateMessage = () => {
        if (loading.isSuccess) {
            switch (loading.value) {
                case Loading.loaded:
                    setMessage(null)
                    break
                case Loading.loading:
                    setMessage(<Fade in={true} unmountOnExit >
                        <CircularProgress />
                    </Fade>)
                    break
                case Loading.success:
                    enqueueSnackbar('Документът е записано', { variant: "success" })
                    setMessage(null)
                    break
            }
        } else {
            enqueueSnackbar(loading.error, { variant: "warning" })
            setMessage(null)
        }
    }

    useEffect(() => {
        updateMessage()
    }, [loading])

    return <Grid2 container sx={{ p: 2 }} xs={12} spacing={2} component={Card} >
        <form onSubmit={handleSubmit}>
            <Grid2 md={12} sm={12} container spacing={2}>
                <Grid2 md={6} sm={12}>
                    <DocumentIDTextField
                        disabled={loading.value === Loading.loading}
                        value={documentID}
                        setValue={updateDocumentID}
                        hasError={errors.documentID != null} />
                </Grid2>
                <Grid2 md={6} sm={12}>
                    <DropdownSelectField
                        disabled={loading.value === Loading.loading}
                        label="Брой вноски"
                        value={paymentsCount}
                        setValue={updatePaymentCounts}
                        items={paymentItems}
                        id="payments-count-id"
                    />
                </Grid2>
            </Grid2>
            <Grid2 md={12} sm={12} container spacing={2}>
                <Grid2 md={6} sm={12}>
                    <FormattedInputs
                        disabled={loading.value === Loading.loading}
                        value={mobileNumber}
                        setValue={updateMobileNumber}
                        hasError={errors.mobileNumber != null} />
                </Grid2>
                <Grid2 md={6} sm={12}>
                    <DatePickerField
                        disabled={loading.value === Loading.loading}
                        label='Първа вноска на'
                        value={date}
                        setValue={updateDate} />
                </Grid2>
            </Grid2>
            <Grid2 md={12} sm={12} container spacing={2}>
                <Grid2 md={6} sm={12}>
                    <PlainTextField
                        disabled={loading.value === Loading.loading}
                        label="Име"
                        id="firstName"
                        hasError={errors.firstName != null}
                        value={firstName} setValue={updateFirstName} />
                </Grid2>
                <Grid2 md={6} sm={12}>
                    <PlainTextField
                        disabled={loading.value === Loading.loading}
                        label="Фамилия"
                        id="lastName"
                        hasError={errors.lastName != null}
                        value={lastName} setValue={updateLastName} />
                </Grid2>
            </Grid2>
            <Grid2 md={12} sm={12} container spacing={2}>
                <Grid2 md={6} sm={12}>
                    <CarNumberTextField
                        disabled={loading.value === Loading.loading}
                        value={carNumber}
                        setValue={updateCarNumber}
                        hasError={errors.carNumber != null} />
                </Grid2>
            </Grid2>
            <Stack sx={{ width: '100%', padding: "15px", height: "48px" }} spacing={2}>
                {mainError() != undefined ? <Alert severity="error"> {mainError()} </Alert> : null}
                {loading != undefined ? <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                >
                    {message}
                </Stack> : null}
            </Stack>
            <Grid2 container md={12}>
                <Grid2>
                    <Button variant="contained" onClick={handleSubmit} disabled={loading.value === Loading.loading}>Запиши</Button>
                </Grid2>
            </Grid2>
        </form >
    </Grid2 >
}

export default AddEntryComponent